'use client';

import { type IdentityInfo } from '@episerver/forms-sdk';
import dynamic from 'next/dynamic';
import React, { useEffect, useState } from 'react';

import styles from './headless-optimizely-form.module.scss';

type HeadlessOptimizelyFormProps = {
  formKey?: string;
  locale?: string;
  prefillData?: Record<string, unknown>;
};

// Dynamically import the browser-only Form component so it is never evaluated on the server.
const EpiserverForm = dynamic(
  async () => {
    const formsReactModule = await import('./forms-react/src');
    return formsReactModule.Form;
  },
  { ssr: false }
);

const requestAccessToken = async (): Promise<string> => {
  const response = await fetch('/api/azure-b2c-token', {
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
  });

  if (!response.ok) {
    const errorData = (await response.json()) as { error?: string };
    throw new Error(
      errorData.error ?? `Failed to obtain Azure B2C AD access token. Status: ${response.status}`
    );
  }

  const json = (await response.json()) as { access_token?: string };
  if (!json.access_token) {
    throw new Error('Azure B2C AD token response did not include access_token.');
  }

  return json.access_token;
};

export const HeadlessOptimizelyForm = ({
  formKey,
  locale,
  prefillData,
}: HeadlessOptimizelyFormProps) => {
  const [currentPageUrl, setCurrentPageUrl] = useState<null | string>(null);
  const [formsBaseUrl, setFormsBaseUrl] = useState<null | string>(null);
  const [identityInfo, setIdentityInfo] = useState<IdentityInfo | null>(null);

  const FORM_KEY = formKey;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setCurrentPageUrl(window.location.href);
      if (process.env.NEXT_PUBLIC_OPTIMIZELY_FORMS_URL) {
        setFormsBaseUrl(process.env.NEXT_PUBLIC_OPTIMIZELY_FORMS_URL);
      }
    }
  }, []);

  // Fetch and cache an access token in FormCache, and keep identityInfo in sync.
  useEffect(() => {
    if (!formsBaseUrl) return undefined;

    let isCancelled = false;
    let intervalId: number | undefined;

    const init = async () => {
      const sdk = await import('@episerver/forms-sdk');
      const { FormCache, FormConstants } = sdk;

      const formCache = new FormCache();

      const ensureToken = async (): Promise<void> => {
        if (isCancelled) return;

        const cachedToken = formCache.get<string>(FormConstants.FormAccessToken);
        if (cachedToken) {
          setIdentityInfo({ accessToken: cachedToken } as IdentityInfo);
          return;
        }

        try {
          const newToken = await requestAccessToken();
          if (isCancelled) return;

          formCache.set<string>(FormConstants.FormAccessToken, newToken);
          setIdentityInfo({ accessToken: newToken } as IdentityInfo);
        } catch (error) {
          console.error('Failed to obtain Azure B2C AD access token.', error);
        }
      };

      await ensureToken();

      // Periodically check if the token was cleared (e.g. after a 401 in FormBody)
      // and automatically request a new one if needed.
      let pollCount = 0;
      const maxPolls = 3;

      intervalId = window.setInterval(async () => {
        if (isCancelled) return;

        pollCount += 1;
        const cachedToken = formCache.get<string>(FormConstants.FormAccessToken);
        if (!cachedToken) {
          await ensureToken();
        }

        if (pollCount >= maxPolls && intervalId !== undefined) {
          window.clearInterval(intervalId);
        }
      }, 5000);
    };

    (async () => {
      try {
        await init();
      } catch (error) {
        console.error('Failed to initialize Azure B2C AD token handling.', error);
      }
    })();

    return () => {
      isCancelled = true;
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
    };
  }, [formsBaseUrl]);

  if (!FORM_KEY || !formsBaseUrl || !currentPageUrl) {
    // If the form is not configured, or we're not yet on the client, render nothing.
    return null;
  }

  return (
    <div className={styles['headless-optimizely-form']}>
      <EpiserverForm
        baseUrl={formsBaseUrl}
        currentPageUrl={currentPageUrl}
        formKey={FORM_KEY}
        identityInfo={identityInfo ?? undefined}
        key={FORM_KEY}
        language={locale ?? 'en'}
        prefillData={prefillData}
      />
    </div>
  );
};
